import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DropDown from "../atomic/DropDown";
import {
  resetDropdownValue,
  setDropdownValue,
  setFileValue,
  setHistoryId,
} from "../../slice/storage";
import { authHeader } from '../../utils/authHeaders';
import toast, { Toaster } from 'react-hot-toast';
import { useToast } from '../Toast';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Modal from "./Modal";
import { fetchUserFiles } from "../../slice/apiSlice";
import constants, { BASE_URL } from "../constants";
import {
  addUserMessage,
  clearChatHistory,
  sendChatMessage,
  startWordGeneration,
  addPendingWordJob,
  startDataAnalysis,
  addPendingAnalysisJob,
  clearError,
  setAnalyzingImage,
  startSendLoading,
  stopSendLoading,
} from "../../slice/chatSlice";
import WordIcon from "../icons/WordIcon";
import { v4 as uuidV4 } from "uuid";
import { fetchTasksAsync } from "../../slice/taskSlice";
import { uploadFilesWithHistory, createChatHistory, updateHistoryName } from "../../Api/req";
import { fetchChatHistory, fetchChatHistoryByUser } from "../../slice/api/historySlice";
import { getTaskStatusesByHistoryAsync } from '../../slice/taskStatusSlice';
import { selectAIProfile, selectAIProfileTaskId, fetchAIProfileByTaskAsync, clearAIProfile } from "../../slice/aiProfileSlice";
import { fetchDefaultFiles } from "../../slice/defaultFilesSlice";
import { SlidersHorizontal, Image as ImageIcon, SquareTerminal } from "lucide-react";
import { ANALYSIS_BLUE, ANALYSIS_BLUE_SOFT, ANALYSIS_BLUE_BORDER } from "../atomic/AnalysisJobCard";
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { useAnalysisMode } from '../../hooks/useAnalysisMode';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';
import * as pdfjsLib from 'pdfjs-dist';

// Ensure pdfjs worker is set up for counting pages
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ACCEPTED_FILE_TYPES = {
  "application/pdf": ".pdf",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "text/csv": ".csv",
  "application/csv": ".csv",
  "application/vnd.ms-powerpoint": ".ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
  "text/plain": ".txt",
  // Both .jpg and .jpeg report MIME type "image/jpeg" — since this map's
  // keys aren't otherwise read anywhere (only Object.values() is, for the
  // extension allowlist/accept attribute below), a second, non-standard key
  // is used here just so BOTH extensions land in that values list; a file
  // named "photo.jpeg" would otherwise fail validateFile's extension check
  // since only ".jpg" would be present.
  "image/jpeg": ".jpg",
  "image/jpg": ".jpeg",
  "image/png": ".png",
};

// Document Intelligence analyzes an image directly (no page-splitting to
// count, unlike a PDF) — each image upload is always exactly 1 page for OCR
// quota purposes. Capped at 3 per batch (separate from the overall
// max-files-per-analysis limit) since each is its own individual OCR call,
// not pages of one document — a much larger per-message batch would be an
// easy way to blow through an org's OCR quota in a single click.
const MAX_IMAGES_PER_UPLOAD = 3;
const isImageFile = (file) => file?.type?.startsWith('image/');

const BUTTON_STATES = {
  disabled: {
    className: "bg-gray-300 cursor-not-allowed",
    hover: "",
  },
  enabled: {
    className: "bg-custom-yellow opacity-80",
    hover: "hover:bg-custom-yellow hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-yellow",
  },
};

const ChatInput = ({
  taskID,
  taskName,
  project,
  historyId,
  onClick,
  searchType,
  prompt,
  activateWordModeSignal,
  activateAnalysisModeSignal,
  toggleSearchSection,
  onHistoryCreated,
  chatName,
  setChatName,
  chatSettings,
  allowLocalFiles = true,
  // When a Privacy disclaimer banner sits directly above this component
  // (Analysis.jsx), the input's own top padding pushes it away from the
  // banner, breaking the "attached, no gap" look — this drops just that
  // one side while leaving the other three untouched everywhere else
  // ChatInput is used (e.g. Chat.jsx, which never passes this).
  hideTopPadding = false,
}) => {
  const notify = () => toast('Here is your toast.');
  const user_id = localStorage.getItem("id");
  const task_id = useSelector((state) => state.dropdown.id);
  const task_name = useSelector((state) => state.dropdown.task);
  const userFiles = useSelector((state) => state.api.userFiles);
  const location = useLocation();
  const dispatch = useDispatch();
  const selectedValue = useSelector((state) => state.dropdown.task);
  const aiProfile = useSelector(selectAIProfile);
  const aiProfileTaskId = useSelector(selectAIProfileTaskId);
  // Pure Web-only agents have no document knowledge base at all — same
  // formula Analysis.jsx uses — so the "/" restrict-to-file picker (which
  // lists the agent's uploaded knowledge files) has nothing valid to offer.
  const isPureWebOnly = aiProfile?.knowledge_source
    ? aiProfile.knowledge_source === 'web'
    : !!aiProfile?.web_search_enabled;
  const profileIsCurrent = aiProfileTaskId != null && String(aiProfileTaskId) === String(taskID || task_id);
  const codeInterpreterEnabled = profileIsCurrent && Boolean(aiProfile?.code_interpreter_enabled);
  const wordEnabled = !aiProfile || Boolean(aiProfile.word_enabled);
  const [files, setFiles] = React.useState([]);
  // Images picked/pasted/dropped during an ACTIVE chat session are staged
  // here first (with a local object-URL thumbnail), not uploaded yet — same
  // "confirm before it actually goes to the sidebar" UX ChatGPT's attach
  // button has. Only PDFs/Office files keep uploading immediately on
  // selection; a thumbnail preview only makes sense for images anyway.
  // Cleared (and each object URL revoked) once handleSendMessage uploads
  // them for real, or the user removes one before ever sending.
  const [pendingImages, setPendingImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isConverting, setIsConverting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const userId = localStorage.getItem("id");
  const tasks = useSelector((state) => state.tasks.tasks);
  const tasksStatus = useSelector((state) => state.tasks.status);
  const tasksError = useSelector((state) => state.tasks.error);
  const { addToast, ToastContainer } = useToast();
  const [history_name, setHistoryName] = useState('');
  const { chatHistory = [], status, error1 } = useSelector((state) => state.history);
  const { error: chatError, errorCode: chatErrorCode, loading: chatLoading, loadingHistoryId: chatLoadingHistoryId, analyzingImageHistoryId } = useSelector((state) => state.chat || { error: null, errorCode: null, loading: false, loadingHistoryId: null, analyzingImageHistoryId: null });
  const [showSearchSection, setShowSearchSection] = useState(false);
  const hname = chatHistory?.history_records;
  const [chatInput, setChatInput] = useState("");
  const textareaRef = useRef(null);
  // "/" file picker — restricts the NEXT question to one or more specific
  // imported files (Dateiname filter server-side, OR'd together when there's
  // more than one) instead of searching the whole task. Fully decoupled from
  // chatInput — opening it (button click, or typing a leading "/") never
  // leaves "/" sitting in the visible textarea; its own search box below
  // drives filePickerQuery instead. Stays open across picks so several files
  // can be selected one after another; each pick toggles a chip instead of
  // closing the dropdown (see selectRestrictFile). Each entry carries the
  // file plus whichever Knowledge Area/Site group it was picked from (a site
  // is often tagged under several areas at once, so a bare folder path alone
  // can't tell two selections apart — see getSelectedFileDisplayPath below).
  const [selectedRestrictFiles, setSelectedRestrictFiles] = useState([]); // [{file, areaLabel}]
  // Word toggle — per-message, resets after sending or switching chats (see
  // the historyId effect below), never automated from message content (spec:
  // "Do not automate this — every passing mention of Word would then produce
  // a document").
  const [wordModeOn, setWordModeOn] = useState(false);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const [filePickerSearchText, setFilePickerSearchText] = useState('');
  const filePickerSearchRef = useRef(null);
  const filePickerContainerRef = useRef(null);
  const filePickerTriggerRef = useRef(null);
  // null = picker is showing the top-level Knowledge Area/Site group list; a
  // group key = picker has drilled into that group and is browsing its real
  // SharePoint folder tree (see filePickerSubfolderPath below).
  const [filePickerActiveFolder, setFilePickerActiveFolder] = useState(null);
  // Once inside a group, its files can still be nested in real SharePoint
  // sub-folders (e.g. "01 Anleitungen" > "06 Test") — this is the drill-down
  // trail of folder NAMES within that group's own tree, deepest last. Empty
  // = browsing the group's root level.
  const [filePickerSubfolderPath, setFilePickerSubfolderPath] = useState([]);
  const closeFilePicker = () => {
    setIsFilePickerOpen(false);
    setFilePickerSearchText('');
    setFilePickerActiveFolder(null);
    setFilePickerSubfolderPath([]);
  };
  // Clicking anywhere outside the open dropdown closes it, same as any other
  // popover/menu in the app — otherwise it stays open indefinitely since
  // picking a file no longer auto-closes it (see selectRestrictFile).
  useEffect(() => {
    if (!isFilePickerOpen) return;
    const handleClickOutside = (e) => {
      // Excludes the trigger button too — otherwise re-clicking "/" while
      // already open closes (via this mousedown handler) then immediately
      // reopens (via the button's own click), silently resetting whatever
      // folder/search state was there instead of leaving it alone.
      if (
        filePickerContainerRef.current && !filePickerContainerRef.current.contains(e.target) &&
        filePickerTriggerRef.current && !filePickerTriggerRef.current.contains(e.target)
      ) {
        closeFilePicker();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilePickerOpen]);
  // Focus the picker's own search box the moment it opens, whether that was
  // the button click or typing a leading "/" — keyboard input should land
  // there, not in the (now empty) chat textarea.
  useEffect(() => {
    if (isFilePickerOpen) {
      filePickerSearchRef.current?.focus();
    }
  }, [isFilePickerOpen]);

  // The textarea auto-grows by writing scrollHeight onto its inline style on
  // every keystroke. After sending, chatInput resets to '' but the inline
  // height stays inflated — leaving an empty multi-line box where the
  // placeholder appears at the top. Reset it back to a single-line height
  // whenever the value clears.
  useEffect(() => {
    if (chatInput === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [chatInput]);
  const { historyId: urlHistoryId } = useParams();
  const [localHistoryId, setLocalHistoryId] = useState(historyId || urlHistoryId || "");
  const analysisChatId = localHistoryId || urlHistoryId || '';
  const chatHistoryRecord = useSelector((state) => state.history.chatHistory?.history_records);
  const savedAnalysisModeOn = analysisChatId && chatHistoryRecord?.id === analysisChatId
    ? Boolean(chatHistoryRecord.analysis_mode_on)
    : undefined;
  const [analysisModeOn, setAnalysisMode, adoptNewChatAnalysisMode] = useAnalysisMode(
    analysisChatId,
    savedAnalysisModeOn,
    profileIsCurrent ? aiProfile : null,
    codeInterpreterEnabled,
    () => toast.error(t('dataAnalysis.modeSaveError')),
  );
  // Clicking the Word hint under the last answer (ChatBlob.jsx) bumps this
  // counter — turn Word mode on and focus the input, same click either way
  // even if it's already on.
  useEffect(() => {
    if (!activateWordModeSignal) return;
    setWordModeOn(true);
    setAnalysisMode(false);
    textareaRef.current?.focus();
  }, [activateWordModeSignal, setAnalysisMode]);
  useEffect(() => {
    if (!activateAnalysisModeSignal) return;
    setAnalysisMode(true);
    setWordModeOn(false);
    textareaRef.current?.focus();
  }, [activateAnalysisModeSignal, setAnalysisMode]);
  const { data: userData } = useSelector((state) => state.admin);
  const { files: defaultFiles, loading: defaultFilesLoading } = useSelector((state) => state.defaultFiles);
  const { isRecording, isTranscribing, formattedDuration, startRecording, stopRecording, cancelRecording } = useVoiceRecorder();
  // Waveform bar count derived from the measured container width (see the
  // recording-state waveform below) instead of a hardcoded count, so it
  // fills the actual available space (+ icon to X button) rather than
  // always occupying a fixed ~350px right-aligned block.
  const waveformContainerRef = useRef(null);
  const [waveformBarCount, setWaveformBarCount] = useState(100);
  useEffect(() => {
    if (!isRecording) return;
    const el = waveformContainerRef.current;
    if (!el) return;
    const BAR_PITCH = 3.5; // 2px bar width + 1.5px gap
    const MIN_BARS = 20;
    const updateBarCount = () => {
      const width = el.clientWidth;
      if (width > 0) {
        setWaveformBarCount(Math.max(MIN_BARS, Math.floor(width / BAR_PITCH)));
      }
    };
    updateBarCount();
    const observer = new ResizeObserver(updateBarCount);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isRecording]);
  const { primaryColor } = useThemeColor();
  const { t, language } = useTranslation();

  // Whether THIS chat currently has a response in flight — typing stays
  // enabled the whole time (ChatGPT-style), only sending is blocked until
  // it settles. `chatLoading` alone isn't enough to scope this to the
  // current chat: it's global Redux state, so if a different chat's
  // request were still in flight (e.g. the user navigated away from it
  // before it finished), the raw flag would incorrectly block this chat's
  // input too, since Redux doesn't shut off "loading" per chat, just per
  // in-flight request. Comparing against `chatLoadingHistoryId` (the
  // specific chat the in-flight request belongs to) scopes it correctly,
  // matching the same pattern Analysis.jsx already uses.
  const isResponsePending = chatLoading && chatLoadingHistoryId === (localHistoryId || urlHistoryId);

  // Only the image-analysis span specifically (upload -> vision-LLM ->
  // search-indexing) — NOT the whole isResponsePending duration after that.
  // Once the question is actually sent, the real answer-generation progress
  // (WebSearchLoadingIndicator's "Checking useful sources" steps, or the
  // bouncing dots) already gives its own visible feedback in the chat
  // itself; keeping the Send button spinning through that entire stretch
  // too was redundant and, worse, made it look stuck/broken once that
  // other indicator had clearly taken over.
  const isImageAnalyzing = analyzingImageHistoryId === (localHistoryId || urlHistoryId);

  // A just-uploaded file (image or otherwise) is only searchable/citable
  // once Document Intelligence has actually indexed it — asking a question
  // about it while its Sidebar status is still "Pending" would just get
  // answered with no knowledge of it at all. Blocks sending the same way
  // isResponsePending does, until every uploaded file for this chat reports
  // 'done'.
  const hasPendingFiles = Boolean(userFiles?.files?.some((f) => f.status === 'Pending'));

  // The picker's own search box (isFilePickerOpen/filePickerSearchText
  // above) drives this now, not chatInput — null means the picker is closed,
  // same meaning downstream as before.
  const filePickerQuery = isFilePickerOpen ? filePickerSearchText : null;
  // Full folder + filename for a restricted file — SharePoint's own
  // sharepoint_file_path already IS "folder/.../filename", just possibly
  // leading-slashed; local/Standard files have no folder to prepend. The
  // Knowledge Area/Site group label (captured at selection time, since one
  // file can sit under several areas) goes in front of that when present,
  // so two otherwise-identical folder paths picked from different area
  // groups still read as distinct. Shown in the chip AND prepended to the
  // sent/saved question text below, so what the user sees selected and
  // what gets saved always match.
  const getSelectedFileDisplayPath = (file, areaLabel) => {
    const folderAndFile = file.sharepoint_file_path
      ? file.sharepoint_file_path.replace(/^\/+/, '')
      : file.filename;
    return areaLabel ? `${areaLabel}/${folderAndFile}` : folderAndFile;
  };
  // Mirrors Sidebar.jsx's own split (userUploadedFiles/defaultFiles ->
  // sharePointDefaultFiles/otherDefaultFiles): SharePoint-origin files get
  // grouped into a Knowledge Area > Site tree (a real folder to drill into),
  // while every local/manually-uploaded file — default or chat-uploaded —
  // is "Standard" and listed directly, no folder needed. Same field
  // (sharepoint_site_id presence) Sidebar uses to decide which bucket a
  // file falls into.
  const pickerStandardFiles = (userFiles?.files || []).filter((f) => !f.sharepoint_site_id);
  // One group per (Knowledge Area, Site) a SharePoint file belongs to — a
  // file tagged with multiple areas appears once under each, same as the
  // Sidebar tree, so the label here is always something the user already
  // recognizes from their own file browser instead of a raw SharePoint
  // folder path.
  const pickerSharePointGroups = Object.values(
    (userFiles?.files || []).filter((f) => f.sharepoint_site_id).reduce((acc, f) => {
      const areas = (f.knowledge_area_names && f.knowledge_area_names.length > 0)
        ? f.knowledge_area_names
        : [t('sidebar.noKnowledgeArea')];
      areas.forEach((areaName) => {
        const key = `${areaName}::${f.sharepoint_site_id}`;
        if (!acc[key]) acc[key] = { key, label: `${areaName} — ${f.sharepoint_site_name || ''}`, files: [] };
        acc[key].files.push(f);
      });
      return acc;
    }, {})
  );
  // Typing after "/" switches to a global filename search (all sites/folders,
  // like Sidebar's own search) instead of the current-level-only navigation.
  const isPickerSearching = filePickerQuery !== null && filePickerQuery !== '';
  const filePickerSearchResults = isPickerSearching
    ? (userFiles?.files || [])
        .filter((f) => f.filename && f.filename.toLowerCase().includes(filePickerQuery.toLowerCase()))
        .map((f) => {
          let areaLabel = null;
          if (f.sharepoint_site_id) {
            const areas = (f.knowledge_area_names && f.knowledge_area_names.length > 0)
              ? f.knowledge_area_names
              : [t('sidebar.noKnowledgeArea')];
            areaLabel = `${areas.join(', ')} — ${f.sharepoint_site_name || ''}`;
          }
          // Folder segments only (drop the filename itself) — kept separate
          // from areaLabel so the subtitle line below can show it without
          // fragile string-slicing on the combined path.
          const folderOnly = f.sharepoint_file_path
            ? f.sharepoint_file_path.split('/').filter(Boolean).slice(0, -1).join('/')
            : '';
          const folderPath = [areaLabel, folderOnly].filter(Boolean).join('/');
          return {
            file: f,
            areaLabel,
            folderPath,
            displayPath: getSelectedFileDisplayPath(f, areaLabel),
          };
        })
        .slice(0, 40)
    : [];
  // Top-level "/" list mixes both: SharePoint groups (click drills in) ahead
  // of standalone Standard files (click selects immediately) — same order
  // Sidebar renders its SHAREPOINT section above Standard.
  const filePickerTopEntries = filePickerQuery === '' && !filePickerActiveFolder
    ? [
        ...pickerSharePointGroups
          .filter((group) => group.label.toLowerCase().includes(filePickerQuery.toLowerCase()))
          .map((group) => ({ type: 'folder', key: group.key, label: group.label, count: group.files.length })),
        ...pickerStandardFiles
          .filter((f) => f.filename && f.filename.toLowerCase().includes(filePickerQuery.toLowerCase()))
          .map((f) => ({ type: 'file', key: f.id || f.filename, file: f })),
      // The dropdown itself already scrolls (max-h-64 overflow-y-auto) — an
      // 8-item cap silently pushed every Standard file off the list on any
      // task with 8+ SharePoint groups (a single site tagged under several
      // Knowledge Areas produces one group per area, so this filled up
      // fast). Typing after "/" already narrows this down live, so a much
      // looser cap is just a sanity bound, not a real limit in practice.
      ].slice(0, 40)
    : [];
  const activeFolderGroup = filePickerActiveFolder
    ? pickerSharePointGroups.find((group) => group.key === filePickerActiveFolder)
    : null;
  // Same recursive tree-building Sidebar.jsx uses for its own SHAREPOINT
  // section (buildFolderTree there) — real nested SharePoint folders (e.g.
  // "01 Anleitungen" containing "06 Test") were being flattened away
  // entirely before, since only the group's flat file list was ever shown.
  const buildPickerFolderTree = (files) => {
    const root = { folders: {}, files: [] };
    files.forEach((file) => {
      const parts = (file.sharepoint_file_path || file.filename || '').split('/').filter(Boolean);
      const folderParts = parts.slice(0, -1);
      let node = root;
      folderParts.forEach((name) => {
        if (!node.folders[name]) node.folders[name] = { name, folders: {}, files: [] };
        node = node.folders[name];
      });
      node.files.push(file);
    });
    return root;
  };
  const activeFolderTree = activeFolderGroup ? buildPickerFolderTree(activeFolderGroup.files) : null;
  // Walk the tree down to wherever filePickerSubfolderPath currently points.
  const activeFolderNode = activeFolderTree
    ? filePickerSubfolderPath.reduce((node, name) => (node && node.folders[name]) || null, activeFolderTree)
    : null;
  const filePickerSubfolders = filePickerQuery === '' && activeFolderNode
    ? Object.values(activeFolderNode.folders).slice(0, 40)
    : [];
  const filePickerFilesInFolder = filePickerQuery === '' && activeFolderNode
    ? activeFolderNode.files.slice(0, 40)
    : [];
  const enterFilePickerFolder = (groupKey) => {
    setFilePickerActiveFolder(groupKey);
    setFilePickerSubfolderPath([]);
  };
  const enterFilePickerSubfolder = (folderName) => {
    setFilePickerSubfolderPath((prev) => [...prev, folderName]);
  };
  // One step back: out of a nested sub-folder first, only leaving the
  // group entirely once already back at its root.
  const backToFilePickerFolders = () => {
    if (filePickerSubfolderPath.length > 0) {
      setFilePickerSubfolderPath((prev) => prev.slice(0, -1));
    } else {
      setFilePickerActiveFolder(null);
    }
  };
  // The same physical file (by item id, or plain id/filename for local
  // uploads) picked twice is a no-op, not a second chip.
  const isSameRestrictedFile = (a, b) => (
    a.sharepoint_item_id || b.sharepoint_item_id
      ? a.sharepoint_item_id === b.sharepoint_item_id
      : a.id
        ? a.id === b.id
        : a.filename === b.filename
  );
  // Clicking an unselected file adds it (chip appears); clicking an already-
  // selected one removes it — same toggle a checkbox list would give, and
  // the only way to deselect from inside the picker itself (the composer
  // chip's own "x" still works too). Picker/folder/search view is left
  // untouched either way so the next file can be picked right away.
  const selectRestrictFile = (file, areaLabel = null) => {
    setSelectedRestrictFiles((prev) => (
      prev.some((entry) => isSameRestrictedFile(entry.file, file))
        ? prev.filter((entry) => !isSameRestrictedFile(entry.file, file))
        : [...prev, { file, areaLabel }]
    ));
  };
  const removeRestrictFile = (index) => {
    setSelectedRestrictFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Shared row for every file entry the picker lists (search results, top-
  // level Standard files, files inside a drilled-into folder) — swaps the
  // file icon for a checkmark in the org's own theme color once picked,
  // instead of a hardcoded color, and labels it "selected" so a re-click
  // reads as a deselect rather than picking it again.
  const renderPickerFileRow = (file, areaLabel, subtitle) => {
    const isSelected = selectedRestrictFiles.some((entry) => isSameRestrictedFile(entry.file, file));
    return (
      <button
        key={file.sharepoint_item_id || file.id || file.filename}
        type="button"
        onClick={() => selectRestrictFile(file, areaLabel)}
        title={getSelectedFileDisplayPath(file, areaLabel)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        {isSelected ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-gray-400">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] text-gray-800 truncate">{file.filename}</span>
          {subtitle && (
            <span className="block text-[12px] text-gray-400 truncate">{subtitle}</span>
          )}
        </span>
        {isSelected && (
          <span className="text-[12px] flex-shrink-0" style={{ color: primaryColor }}>
            {t('chatActions.filePickerSelected')}
          </span>
        )}
      </button>
    );
  };

  // Nothing to actually send: previously this was just "no typed text", which
  // kept the Send button disabled even with images already staged and ready
  // to go — attaching an image with no other text (exactly what ChatGPT's
  // own image attachments allow) could never be sent at all. Any staged
  // image now counts as something to send on its own.
  const hasNothingToSend = !chatInput.trim() && pendingImages.length === 0;

  // Get selected task to access its organization_id
  const currentTaskId = taskID || task_id;
  const selectedTask = tasks.find(t => t.id?.toString() === currentTaskId?.toString());
  const taskOrganizationId = selectedTask?.organization_id;

  // aiProfile is one shared Redux slot, not scoped per agent — without this,
  // switching chats left the PREVIOUS agent's word_enabled (and other
  // settings) showing until something else happened to refetch it, e.g. the
  // Word toggle staying visible for an agent that never enabled it. Clearing
  // first avoids that stale flash; Chat.jsx (task_id "40") never fetched
  // this at all before, so this also fixes the toggle never appearing there.
  useEffect(() => {
    dispatch(clearAIProfile());
    if (currentTaskId) {
      dispatch(fetchAIProfileByTaskAsync(currentTaskId));
    }
  }, [currentTaskId, dispatch]);

  // Add this effect:
  useEffect(() => {
    if (historyId && historyId !== localHistoryId) {
      setLocalHistoryId(historyId);
      // The "/" restricted-file picker's selections are scoped to the chat
      // they were picked in — without this they silently carried over (and
      // got sent along) into whichever chat the user switched to next.
      setSelectedRestrictFiles([]);
      closeFilePicker();
      setWordModeOn(false);
    }
  }, [historyId]);

  // Watch for chat errors and display them. Word-generation failures arrive
  // as a bare {code} with no message (routers/word_generation.py) — known
  // codes get a specific translated toast instead of the generic fallback.
  useEffect(() => {
    if (chatError) {
      const wordErrorMessages = {
        word_not_enabled: t('chatActions.wordNotEnabledError'),
        word_generation_already_running: t('chatActions.wordAlreadyRunningError'),
        data_analysis_already_running: t('dataAnalysis.alreadyRunningError'),
        data_analysis_not_enabled: t('dataAnalysis.notEnabledError'),
        data_analysis_invalid_prompt: t('dataAnalysis.startFailedError'),
        data_analysis_start_failed: t('dataAnalysis.startFailedError'),
      };
      toast.error(wordErrorMessages[chatErrorCode] || chatError);
      dispatch(clearError());
    }
  }, [chatError, chatErrorCode, dispatch, t]);

  // Page type helpers
  const isChatRoute = location.pathname.startsWith('/chat');
  const isAnalysisRoute = location.pathname.startsWith('/analysis');
  const isRoot = location.pathname === '/';
  const isActiveAnalysisSession = isAnalysisRoute && Boolean(urlHistoryId);
  const isLandingAnalysis = (isAnalysisRoute && !urlHistoryId) || isRoot;

  // Show different UI components based on page type
  const showAnalysisSetup = isLandingAnalysis;
  const showChatInput = isChatRoute || isActiveAnalysisSession;

  // Format tasks for dropdown
  const CHAT_TASK_ID = "40";
  const formattedTasks = (Array.isArray(tasks) ? tasks : [])
    .filter(task => task && task.id && task.id.toString() !== CHAT_TASK_ID)
    .map((task) => ({
      id: task.id.toString(),
      task: task.name,
    }));

  const handleToggleSearchSection = () => {
    setShowSearchSection(prev => !prev);
    toggleSearchSection();
  };

  useEffect(() => {
    if (historyId) {
      dispatch(getTaskStatusesByHistoryAsync(historyId));
    }
  }, [historyId, dispatch]);

  useEffect(() => {
    const userEmail = localStorage.getItem('email');
    if (userEmail) {
      dispatch(fetchTasksAsync(userEmail));
    } else {
      console.warn('User email not found in localStorage');
    }
  }, [dispatch]);

  // Watch for tasks errors and display them
  useEffect(() => {
    if (tasksError) {
      console.error('Tasks loading error:', tasksError);
      toast.error(t('chatActions.tasksLoadFailed', { error: tasksError }));
    }
  }, [tasksError]);

  // Fetch default files when task is selected - use task's org, not user's org
  useEffect(() => {
    if (currentTaskId && taskOrganizationId) {
      dispatch(fetchDefaultFiles({
        organizationId: taskOrganizationId,
        taskId: currentTaskId
      }));
    }
  }, [dispatch, currentTaskId, taskOrganizationId]);

  // Default files are now queried directly from DefaultFiles table - no map-to-session needed

  useEffect(() => {
    if (prompt) {
      if (typeof prompt === 'object' && prompt.description) {
        const questionTitle = prompt.title || null;
        handleSendMessage(prompt.description, questionTitle);
      } else if (typeof prompt === 'string') {
        handleSendMessage(prompt, null);
      }
    }
  }, [prompt]);

  const getChatSettingsValue = (key, fallback) => {
    if (taskID === '40' && chatSettings && chatSettings[key] !== undefined && chatSettings[key] !== null) {
      return chatSettings[key];
    }
    return fallback;
  };

  const handleSendMessage = async (message, title = null) => {
    // Single enforcement point for every send path (Enter key, Send button,
    // and an Instruction/Standard Question click, which arrives via the
    // `prompt` prop effect below) — none of them may submit while this
    // chat's previous response hasn't settled yet. Typing itself is never
    // blocked; only this submit path is.
    if (isResponsePending) return;
    if (hasPendingFiles) {
      toast.error(t('chatActions.filesStillProcessing'));
      return;
    }
    let messageStr = typeof message === 'string' ? message : '';
    if (messageStr.trim() === "") {
      // No typed text is fine as long as there's at least one staged image
      // to send with it (ChatGPT allows the same) — the backend's own
      // "prompt" field still needs some text, so a sensible default fills
      // in for it instead of silently doing nothing.
      if (pendingImages.length > 0) {
        messageStr = t('chatActions.defaultImageOnlyMessage');
      } else {
        return;
      }
    }

    // A file restriction picked via "/" travels ONLY as restrict_to_filenames
    // below — prompt itself doubles as both the search embedding input and
    // the raw BM25 query text server-side, so prepending the file path here
    // used to get embedded/searched right along with the actual question,
    // diluting it (and getting worse with every extra selected file).

    setChatInput("");
    let textarea = document.getElementById("prompt");
    if (textarea) {
      textarea.style.height = "auto";
    }

    let currentHistoryId = localHistoryId;
    let currentTaskId = taskID;



    // Ensure task_id is '40' for chat page
    if (location.pathname.startsWith('/chat')) {
      currentTaskId = '40';
    }

    // Flip the loading indicator on right now, before any of the network
    // calls below (brand-new-chat creation, image upload) — otherwise the
    // first message of a new chat only shows it once those calls finish,
    // which is the "loader doesn't show immediately" delay. A brand-new
    // chat doesn't have a history id yet, so one is generated here (reused
    // below instead of generating a second one) so the loader can already
    // be scoped to it.
    const loaderHistoryId = currentHistoryId || uuidV4();
    dispatch(startSendLoading(loaderHistoryId));

          if (!localHistoryId) {
        try {
          // Get user's organization ID from admin state
          const organizationId = userData?.organization_id;

          if (!organizationId) {
            dispatch(stopSendLoading(loaderHistoryId));
            toast.error(t('chatActions.noOrganization'));
            return;
          }

          const newHistoryId = loaderHistoryId;
          const result = await createChatHistory(
            userId,
            currentTaskId,
            newHistoryId,
            "",
            chatName || "",
            currentTaskId === '40' ? 'chat' : 'analyse'
          );
          currentHistoryId = result.history_id;
          currentTaskId = result.task_id || currentTaskId; // fallback to '40' if backend returns null
          if (codeInterpreterEnabled && !isChatRoute) await adoptNewChatAnalysisMode(currentHistoryId);
          setLocalHistoryId(currentHistoryId);
          if (typeof onHistoryCreated === "function") {
            onHistoryCreated(currentHistoryId, currentTaskId);
          }
        } catch (error) {
        let msg = t('chatActions.genericError');
        if (error?.code === 'history_limit_reached') {
          msg = t('homepage.historyLimitReached', { limit: error.limit });
        } else if (error?.response?.data?.detail) {
          msg = error.response.data.detail;
        } else if (error?.message) {
          msg = error.message;
        }
        if (Array.isArray(msg)) {
          msg = msg.map(e => e.msg || e).join(" ");
        }
        dispatch(stopSendLoading(loaderHistoryId));
        toast.error(msg);
        return;
      }
    }

    // Images staged since the last send (paste/drop/pick, previewed as a
    // small thumbnail instead of uploading right away) only actually reach
    // the backend/Sidebar now, alongside the message that references them —
    // same confirm-then-attach UX ChatGPT's own image attachments have.
    let uploadedFileIds = [];
    // Kept alive (NOT revoked) for this message's own chat-bubble thumbnail
    // below — reloading later re-fetches the real uploaded image instead
    // (see ChatBlob.jsx), so these are only ever needed for this live session.
    const attachedImagesForBubble = pendingImages.map((p) => ({ filename: p.file.name, previewUrl: p.previewUrl }));
    if (pendingImages.length > 0) {
      const imagesToUpload = pendingImages.map((p) => p.file);
      setPendingImages([]);
      uploadedFileIds = await uploadFilesToBackend(imagesToUpload, currentHistoryId, currentTaskId);
      // Wait for THIS image's own analysis to finish before asking a
      // question that might be about it — see waitForImagesToProcess above.
      await waitForImagesToProcess(uploadedFileIds, currentHistoryId, currentTaskId);
    }

    dispatch(
      addUserMessage({
        content: messageStr,
        role: "user",
        title: title || "",
        attachedImages: attachedImagesForBubble,
        // Display-only — shows which file(s) this question was restricted
        // to, separately from the (now unpolluted) message content itself.
        restrictedFilenames: selectedRestrictFiles.length
          ? selectedRestrictFiles.map((entry) => entry.file.filename)
          : null
      })
    );
    // Shows the "Creating document..." card immediately instead of the
    // generic "AI is responding" dots for the whole /word/generate round
    // trip — see addPendingWordJob's own comment in chatSlice.js.
    if (wordModeOn) {
      dispatch(addPendingWordJob());
    }

    // --- Unified logic for both chat and analysis ---
    let payload = {
      prompt: messageStr,
      user_id: userId,
      role: "user",
      assistant_role: "bot",
      search_type: searchType,
      system_prompt: getChatSettingsValue('system_prompt', aiProfile?.description),
      num_contexts: getChatSettingsValue('num_contexts', aiProfile?.num_contexts),
      title: title || "",
      comment: "",
      history_id: currentHistoryId,
      task_id: currentTaskId,
      attached_image_ids: uploadedFileIds,
      restrict_to_filenames: selectedRestrictFiles.length
        ? selectedRestrictFiles.map((entry) => entry.file.filename)
        : null,
      // Parallel array, same order — lets the backend filter on the exact
      // file (sharepoint_item_id) instead of just its name, since two
      // different files can share a name in different SharePoint folders
      // (the ambiguity this whole picker exists to resolve). null entries
      // for files with no item_id (local/Standard uploads) fall back to
      // filename-only matching server-side.
      restrict_to_item_ids: selectedRestrictFiles.length
        ? selectedRestrictFiles.map((entry) => entry.file.sharepoint_item_id || null)
        : null,
      // Backend ignores this (not a declared PromptRequest field) — it only
      // round-trips through chatSlice.js's own action.meta.arg echo, so the
      // bot message that results from THIS send can show, per cited file,
      // the one Knowledge Area it was actually picked from — not every area
      // that file happens to be tagged with (see ChatBlob.jsx's
      // resolveFolderPath). Keyed by filename since a citation only ever
      // carries a filename back, not which picker entry it came from.
      restrict_to_folder_labels: selectedRestrictFiles.length
        ? selectedRestrictFiles.reduce((acc, entry) => {
            acc[entry.file.filename] = entry.areaLabel;
            return acc;
          }, {})
        : null,
      // Word generation's own fallback when the request isn't in one of the
      // supported languages (ignored by the normal /question endpoint).
      interface_language: language,
    };
    // Word mode sends the same payload shape to a different endpoint
    // (/word/generate, 202 + job_id) instead of a normal chat answer — the
    // Word backend's PromptRequest-like model just ignores the extra fields
    // (system_prompt/num_contexts/etc.) it doesn't need.
    let sendAction;
    if (analysisModeOn && codeInterpreterEnabled && !isChatRoute) {
      const analysisLocalId = uuidV4();
      dispatch(addPendingAnalysisJob({
        localId: analysisLocalId,
        files: selectedRestrictFiles.map((entry) => entry.file.filename),
        scope: selectedRestrictFiles.length ? 'selected' : 'session',
        payload,
      }));
      sendAction = startDataAnalysis({ ...payload, local_id: analysisLocalId });
    } else {
      sendAction = wordModeOn ? startWordGeneration(payload) : sendChatMessage(payload);
    }
    dispatch(sendAction).finally(() => {
      // Nav's sidebar and the Homepage "Recent" list both hold their own
      // stale Redux snapshot (state.history.userHistories) fetched once on
      // mount/route-change — sending a message here never navigates, so
      // without this they'd keep showing this chat by its old position
      // (creation order) until the user happened to leave and come back.
      // The backend already bumped History.last_activity_at for this chat
      // as soon as the question was saved, so a plain refetch now is enough
      // to reflect it — refresh regardless of whether the response itself
      // succeeded, since the user's message was saved either way.
      dispatch(fetchChatHistoryByUser(userId));
    });
    // Per-message, same as pendingImages above — the restriction applies to
    // THIS question only, not the rest of the conversation.
    setSelectedRestrictFiles([]);
    // The Word toggle applies to that one message only (spec) — resets right
    // after sending, same as the restricted-files picker above.
    setWordModeOn(false);

    // Direct Chat Mode sessions start with a placeholder name (no request-setup
    // form was shown to collect one) — rename the session from the first
    // question the user actually asks, so it shows up meaningfully everywhere
    // the title is used (sidebar, chat header, share modal, etc.).
    if (project === t('homepage.newChatPlaceholderName') && currentHistoryId) {
      const derivedName = messageStr.trim().slice(0, 60);
      updateHistoryName(currentHistoryId, derivedName)
        .then(() => {
          dispatch(fetchChatHistory({ historyId: currentHistoryId, userId }));
        })
        .catch(() => {});
    }

    textarea = document.getElementById("prompt");
    if (textarea) {
      textarea.style.height = "auto";
    }
  };

  // File fetching is handled ONLY by Sidebar component to prevent duplicate fetches

  const handleSelect = (value) => {
    dispatch(setDropdownValue(value));
  };

  const handleFileClick = () => {
    if (!allowLocalFiles) return;
    fileInputRef.current?.click();
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleYes = () => {
    closeModal();
    dispatch(clearChatHistory());
    // Check if user has "No License" role
    if (userData?.user_role === 'No License') {
      navigate('/hilfe');
      return;
    }
    navigate(`/`);
  };

  const handleNo = () => {
    closeModal();
  };

  const validateFile = (file) => {
    if (!file) return { isValid: false, error: t('chatActions.noFileSelected') };

    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const isValidType = Object.values(ACCEPTED_FILE_TYPES).includes(fileExtension);
    if (!isValidType) {
      return {
        isValid: false,
        error: t('chatActions.invalidFileType', { types: Object.values(ACCEPTED_FILE_TYPES).join(", ") }),
      };
    }

    return { isValid: true, error: null };
  };

  // Actually uploads a batch of already-validated files to the backend
  // (/api/uploader call — server counts pages and checks the per-session
  // limit itself now — then Sidebar refresh). Factored out of the
  // active-session branch below so handleSendMessage can call it a second
  // time, later, for images that were staged in pendingImages instead of
  // uploaded immediately.
  const uploadFilesToBackend = async (filesToUpload, currentHistoryId, currentTaskIdForUpload) => {
    if (filesToUpload.length === 0) return [];
    const organizationId = userData?.organization_id;
    try {
      const page_counts = filesToUpload.map(f => f._pageCount || 0);
      const response = await uploadFilesWithHistory(
        filesToUpload,
        userId,
        currentTaskIdForUpload,
        currentHistoryId,
        page_counts
      );

      dispatch(fetchUserFiles({
        userId: userId,
        historyId: currentHistoryId,
        taskId: currentTaskIdForUpload,
        organizationId,
      }));

      toast.success(t('chatActions.uploadSuccess'));
      // Backend-assigned File.id per uploaded file — the ONLY reliable way
      // to later find THIS specific upload's own status. Filename alone
      // isn't unique: pasting the same clipboard screenshot into two
      // different messages (its OS-assigned name is otherwise identical
      // both times) previously matched an OLDER, already-"done" file with
      // the same name and treated the brand new, still-processing one as
      // already finished — so the question got answered before its actual
      // image content ever reached the search index.
      return (response?.files || [])
        .filter((f) => f.status === 'success' && f.id)
        .map((f) => f.id);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(
        error?.code === 'upload_page_limit_exceeded'
          ? t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted })
          : error?.message || t('chatActions.uploadFailed')
      );
      return [];
    }
  };

  // Uploading an image (see uploadFilesToBackend above) only creates its
  // File record and kicks off the vision-LLM analysis + search-indexing as a
  // BACKGROUND task on the server — the upload call itself returns almost
  // immediately, long before that analysis finishes. Sending the question
  // that references this same just-attached image right after the upload
  // call (without this wait) asked it before the image's content ever
  // reached the search index, so the model answered with no knowledge of
  // the image at all — indistinguishable from a bad/irrelevant response,
  // but really just a race. Polls the same Sidebar-files endpoint used
  // everywhere else until every one of THESE SPECIFIC file IDs (not
  // filenames — pasting the same clipboard screenshot into two different
  // messages gets the identical OS-assigned name both times, so matching by
  // name could find an older, already-"done" file with the same name and
  // wrongly treat a brand new, still-processing upload as finished) has
  // left "Pending" (done or FAILED — FAILED still unblocks, so a single
  // broken image never wedges every future message in this chat).
  const waitForImagesToProcess = async (fileIds, currentHistoryId, currentTaskIdForUpload) => {
    if (fileIds.length === 0) return;
    const organizationId = userData?.organization_id;
    const maxAttempts = 40; // ~80s at 2s intervals — a generous ceiling for a vision-LLM call + embedding + indexing
    const pollIntervalMs = 2000;
    // An inline "Analyzing image…" placeholder in the chat itself — see
    // Analysis.jsx's analyzingImageHistoryId rendering — instead of a
    // floating toast, which could end up stacked/duplicated if this ever
    // got triggered more than once for the same send.
    dispatch(setAnalyzingImage(currentHistoryId));
    try {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        let result;
        try {
          result = await dispatch(fetchUserFiles({
            userId,
            historyId: currentHistoryId,
            taskId: currentTaskIdForUpload,
            organizationId,
          })).unwrap();
        } catch (e) {
          break; // Can't confirm status — don't hold the message hostage indefinitely
        }
        const relevantFiles = (result?.files || []).filter((f) => fileIds.includes(f.id));
        const allSettled = relevantFiles.length >= fileIds.length && relevantFiles.every((f) => f.status !== 'Pending');
        if (allSettled) break;
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }
    } finally {
      dispatch(setAnalyzingImage(null));
    }
  };

  // Shared by the file-picker input's onChange AND the new paste/drag-drop
  // entry points below — all three just need to gather a plain array of
  // File objects and hand it off here; every validation/page-count/upload
  // rule (extension allowlist, MAX_IMAGES_PER_UPLOAD, PDF page counting,
  // office-to-PDF conversion, the immediate-upload-vs-queue-for-first-message
  // branching) then behaves identically no matter which entry point supplied
  // the files.
  const processIncomingFiles = async (newFiles) => {
    setIsProcessing(true);
    setError(null);

    const validFiles = [];
    // Starts from however many images are ALREADY staged in pendingImages
    // (from an earlier, separate paste/drop/pick this same message hasn't
    // sent yet) — otherwise two 2-image pastes in a row would both pass
    // this batch's own check independently and stage 4 total, silently
    // over MAX_IMAGES_PER_UPLOAD.
    let acceptedImageCount = pendingImages.length;

    for (const file of newFiles) {
      const { isValid, error } = validateFile(file);
      if (isValid) {
        if (file.type === 'application/pdf') {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            file._pageCount = pdf.numPages; // Attach page count
            await pdf.destroy();
          } catch (e) {
            toast.error(t('chatActions.invalidPdfProcessing', { name: file.name }));
            continue;
          }
        } else if (
          file.type.includes('word') ||
          file.type.includes('excel') ||
          file.type.includes('spreadsheet') ||
          file.type === 'text/csv' ||
          file.type === 'application/csv' ||
          /\.(xls|xlsx|csv)$/i.test(file.name)
        ) {
            // Word + Excel + CSV all hit the same /convert-and-count endpoint;
            // the backend converts each format to PDF before storage.
            setIsConverting(true);
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await fetch(`${BASE_URL}/convert-and-count`, {
                    method: 'POST',
                    headers: { ...authHeader() },
                    body: formData,
                });
                const data = await response.json();
                if (response.ok) {
                    file._pageCount = data.page_count;
                } else {
                    toast.error(data.error || t('chatActions.fileConvertFailed'));
                    continue;
                }
            } catch (e) {
                toast.error(t('chatActions.fileConvertFailed'));
                continue;
            } finally {
                setIsConverting(false);
            }
        } else if (isImageFile(file)) {
          // Document Intelligence analyzes an image directly — no pages to
          // split/count like a PDF, so this is always exactly 1 for OCR
          // quota purposes. Capped at MAX_IMAGES_PER_UPLOAD per batch below,
          // separately from the page-count limit checked further down.
          if (acceptedImageCount >= MAX_IMAGES_PER_UPLOAD) {
            toast.error(t('chatActions.tooManyImages', { max: MAX_IMAGES_PER_UPLOAD }));
            continue;
          }
          file._pageCount = 1;
          acceptedImageCount += 1;
        } else {
          file._pageCount = 0;
        }
        validFiles.push(file);
      } else {
        alert(t('chatActions.fileErrorFor', { name: file.name, error }));
      }
    }

    // The per-session upload limit is enforced server-side in /api/uploader
    // at actual upload time now, not here before a session even exists.
    const isActiveAnalysisSession = isAnalysisRoute && Boolean(urlHistoryId);
    if (!isActiveAnalysisSession) {
      setFiles(validFiles.slice(0, 1));

      if (newFiles.length > 1) {
        toast(t('chatActions.onlyOneFileAllowed'), {
          icon: 'ℹ️',
          duration: 3000,
          style: {
            background: '#333',
            color: '#fff',
          },
        });
      }
    } else {
      // Active session: non-image files still upload immediately, exactly
      // as before. Images are staged in pendingImages instead — a small
      // preview thumbnail (like ChatGPT's attach button) lets the user
      // confirm or remove them before anything actually reaches the
      // backend/Sidebar; handleSendMessage uploads them for real once the
      // user actually sends.
      const imageFiles = validFiles.filter(isImageFile);
      const otherFiles = validFiles.filter((f) => !isImageFile(f));

      if (imageFiles.length > 0) {
        setPendingImages((prev) => [
          ...prev,
          ...imageFiles.map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
        ]);
      }

      if (otherFiles.length > 0) {
        const currentHistoryId = localHistoryId || urlHistoryId;
        const currentTaskIdForUpload = task_id || taskID || '40';
        await uploadFilesToBackend(otherFiles, currentHistoryId, currentTaskIdForUpload);
      }
    }

    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (event) => {
    await processIncomingFiles(Array.from(event.target.files || []));
  };

  // Clipboard paste — lets a user paste a copied/screenshotted image
  // straight into the chat instead of saving it to disk first and using the
  // file picker. Only intercepts when the clipboard actually contains an
  // image (checked via item.kind/type, same MIME-based check as
  // isImageFile); anything else (plain text, a copied URL, ...) falls
  // through untouched so normal text pasting keeps working exactly as
  // before.
  const handlePaste = async (event) => {
    if (!allowLocalFiles) return;
    const items = event.clipboardData?.items;
    if (!items || items.length === 0) return;
    // Only the FIRST image item — copying one screenshot/image often puts
    // SEVERAL representations of that SAME picture on the clipboard at once
    // (e.g. macOS commonly offers a screenshot as both PNG and TIFF).
    // Collecting every image-typed item here uploaded the same picture
    // multiple times under the same generic browser-assigned filename
    // (e.g. "image.png"), and the embedder's own already-done guard then
    // silently skipped the second copy's processing entirely (it saw the
    // first copy's already-'done' status for that filename) — leaving that
    // duplicate blob stuck at its original path forever, un-archived, so
    // opening it later failed with "File not found".
    let pastedImage = null;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        pastedImage = item.getAsFile();
        break;
      }
    }
    if (pastedImage) {
      // Prevents the raw image data from also being inserted as pasted text
      // into the textarea (browsers otherwise sometimes paste a filename or
      // blob reference alongside firing this event).
      event.preventDefault();
      await processIncomingFiles([pastedImage]);
    }
  };

  // Drag-and-drop — dropping image files anywhere on the input row attaches
  // them the same way the paste/file-picker paths do. onDragOver must also
  // preventDefault, or the browser's default "not-a-drop-target" behavior
  // (opening the dragged file in the tab) wins instead of firing onDrop.
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    if (!allowLocalFiles) return;
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    if (droppedFiles.length > 0) {
      await processIncomingFiles(droppedFiles);
    }
  };

  const handleUpload = async () => {
    if ((isFileRequired && !files.length) || !userId) return;

    // Use the existing historyId if available
    let currentHistoryId = localHistoryId || urlHistoryId;

    try {
      setIsProcessing(true);

      // Only create a new chat history if there is NO historyId at all
      if (!currentHistoryId) {
        // Get user's organization ID from admin state
        const organizationId = userData?.organization_id;
        
        if (!organizationId) {
          toast.error(t('chatActions.noOrganization'));
          setIsProcessing(false);
          return;
        }
        
        currentHistoryId = uuidV4();
        const chatHistoryResult = await createChatHistory(
          userId,
          task_id || taskID || "40",
          currentHistoryId,
          task_name || taskName || "",
          history_name || "",
          location.pathname === '/chat' ? 'chat' : 'analyse'
        );

        setLocalHistoryId(currentHistoryId); // Store for future use
        console.log("Chat history created:", chatHistoryResult);

        // Default files are now queried directly from DefaultFiles table - no map-to-session needed
        const currentTaskIdForMapping = task_id || taskID || "40";

        // Refresh files after session creation
        dispatch(fetchUserFiles({ userId, historyId: currentHistoryId, taskId: currentTaskIdForMapping, organizationId }));
      }

      // Page counting and the per-session upload limit are enforced
      // server-side in /api/uploader itself now.
      const organizationId = userData?.organization_id;
      const currentTaskIdForUpload = task_id || taskID || "40";

      // Default files are automatically loaded when session is created

      // Upload user files
      if (files.length > 0) {
        const page_counts = files.map(f => f._pageCount || 0);
        const uploadResult = await uploadFilesWithHistory(
          files,
          userId,
          currentTaskIdForUpload,
          currentHistoryId,
          page_counts
        );
        console.log("Upload success:", uploadResult);

        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        
        // Refresh the sidebar to show all files (user + default)
        dispatch(fetchUserFiles({
          userId: userId,
          historyId: currentHistoryId,
          taskId: currentTaskIdForUpload,
          organizationId,
        }));
      }

      // Optionally navigate if you want to show the analysis page for the new chat
      if (!urlHistoryId && !localHistoryId) {
        // Check if user has "No License" role
        if (userData?.user_role === 'No License') {
          navigate('/hilfe');
          return;
        }
        navigate(`/analysis/${currentHistoryId}`);
      }
    } catch (error) {
      setIsProcessing(false);
      let msg = t('chatActions.genericError');
      if (error?.code === 'history_limit_reached') {
        msg = t('homepage.historyLimitReached', { limit: error.limit });
      } else if (error?.code === 'upload_page_limit_exceeded') {
        msg = t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted });
      } else if (error?.response?.data?.detail) {
        msg = error.response.data.detail;
      } else if (error?.message) {
        msg = error.message;
      }
      if (Array.isArray(msg)) {
        msg = msg.map(e => e.msg || e).join(" ");
      }
      toast.error(msg);
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  const fileUploadRequired = selectedTask?.file_upload_required;
  const isFileRequired = fileUploadRequired === true || fileUploadRequired === 1;
  const isChatPage = location.pathname === "/chat";
  const isSubmitEnabled = isChatPage
    ? !isProcessing
    : (selectedValue || taskName) &&
      (!isFileRequired || files.length > 0) &&
      !isProcessing &&
      (history_name || urlHistoryId);

  const getButtonStyles = (isEnabled) => {
    const state = isEnabled ? BUTTON_STATES.enabled : BUTTON_STATES.disabled;
    return `${state.className} ${state.hover}`;
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prevFiles) => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      return newFiles;
    });
  };

  const handleRemovePendingImage = (indexToRemove) => {
    setPendingImages((prev) => {
      const removed = prev[indexToRemove];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, index) => index !== indexToRemove);
    });
  };

  // Revoke any still-staged preview URLs on unmount (chat switched/closed
  // before the user sent) — otherwise each stays in memory until a full
  // page reload, since nothing else ever calls revokeObjectURL for these.
  useEffect(() => {
    return () => {
      pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicClick = async () => {
    if (isRecording) {
      try {
        const transcript = await stopRecording();
        if (transcript) {
          // Paste transcribed text into input so user can review/edit before sending
          setChatInput(transcript);
        }
      } catch (error) {
        addToast(t('chatActions.transcriptionFailed'), 'error');
      }
    } else {
      try {
        await startRecording();
      } catch (error) {
        addToast(t('chatActions.microphoneDenied'), 'error');
      }
    }
  };

  return (
    <footer className="bg-white flex justify-center items-center">
      <Toaster position="top-right"/>
      <div className="flex-1 max-w-4xl">
        <div className={`bg-white w-full ${showAnalysisSetup ? 'rounded-lg shadow-lg' : ''}`}>

          {/* Task selection bar - only show on landing/setup page, NOT during active sessions */}
          {showAnalysisSetup && (
            <div className="flex items-center gap-4 border-b pb-3 pl-3 w-full justify-between">
              <div className="flex items-center gap-3 w-full justify-between">
                <div className={`flex items-center gap-3 text-white ${!selectedValue && !taskName ? 'w-[300px]' : 'w-full'}`}>
                  <DropDown
                    list={formattedTasks}
                    onSelect={handleSelect}
                    selectedValue={taskName ? taskName : selectedValue}
                    disabled={false}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept={Object.values(ACCEPTED_FILE_TYPES).join(",")}
                    style={{ display: "none" }}
                    multiple={false}
                  />
                  {(selectedValue || taskName) && <img className="w-4 h-4" src={constants.arrow}/>}
                  {(selectedValue || taskName) && (
                    <div className="flex flex-col w-full gap-2">
                      <input
                        type="text"
                        onChange={(e) => setHistoryName(e.target.value)}
                        className="w-full px-3 py-3 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white"
                        placeholder={t('chatActions.enterProjectName')}
                        disabled={!selectedValue && !taskName}
                        value={history_name}
                      />
                    </div>
                  )}
                  {(selectedValue || taskName) && (
                    <img className="w-4 h-4" src={constants.arrow} />
                  )}
                  {(selectedValue || taskName) && (
                    <button
                      type="button"
                      onClick={handleFileClick}
                      className={`inline-flex items-center justify-center px-3 py-2.5 w-full border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white ${
                        selectedValue || taskName ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                      }`}
                      disabled={!selectedValue && !taskName}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                        />
                      </svg>
                      {isConverting ? t('chatActions.convertToPdf') : isProcessing ? t('chatActions.processing') : t('chatActions.addFiles')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Files Display (Default + User files combined) */}
          {(defaultFiles.length > 0 || files.length > 0) && (
            <div className="mt-2 px-4">
              {/* User Files */}
              {files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded"
                >
                  <div className="flex items-center">
                    {isImageFile(file) ? (
                      <ImageIcon className="mr-2 h-4 w-4" />
                    ) : (
                      <svg
                        className="mr-2 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    )}
                    {file.name}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    className="text-gray-500 hover:text-red-500 p-1"
                    aria-label={t('chatActions.removeFile')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </li>
              ))}
            </div>
          )}

          {/* Analysis Setup Section - Only for landing/analysis pages */}
          {showAnalysisSetup && (history_name || (hname && hname.history_name)) && (
            <div className="flex align-center justify-center w-full">
              <button
                type="button"
                onClick={handleUpload}
                className={`inline-flex justify-center items-center mt-3 mb-3 px-20 py-2 border border-transparent rounded-md shadow-sm text-white ${getButtonStyles(isSubmitEnabled)}`}
                disabled={!isSubmitEnabled}
              >
                {isProcessing ? t('chatActions.uploading') : t('chatActions.startAnalysis')}
              </button>
            </div>
          )}

          {/* Chat Input Section - Only for chat pages and active analysis sessions */}
          {showChatInput && (
            <div className={`pl-3 pr-3 pb-3 ${hideTopPadding ? 'pt-0' : 'pt-3'}`}>
              {/* Hidden file input for plus button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png"
                style={{ display: "none" }}
              />
              {/* Recording State (waveform UI) — only when actively recording, NOT transcribing */}
              {isRecording ? (
                <div className="flex items-center w-full rounded-full border border-gray-200 pl-4 pr-2 py-2 bg-white">
                  {/* Plus icon (disabled during recording) */}
                  <div className="text-gray-300 flex-shrink-0">
                    <svg width="30" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>

                  {/* Waveform — fills space from + icon to X button */}
                  <div className="flex-1 flex items-center mx-3">
                    <div ref={waveformContainerRef} className="flex items-center justify-end gap-[1.5px] w-full" style={{ height: '38px' }}>
                      {Array.from({ length: waveformBarCount }).map((_, i) => {
                        const total = waveformBarCount;
                        const leadDots = 12;
                        const trailDots = 8;
                        let h, isStatic;
                        if (i < leadDots) {
                          h = 3;
                          isStatic = true;
                        } else if (i >= total - trailDots) {
                          h = 3;
                          isStatic = true;
                        } else {
                          const idx = i - leadDots;
                          const barCount = total - leadDots - trailDots;
                          const progress = idx / barCount;
                          const wave = Math.sin(idx * 0.4) * 8 + Math.sin(idx * 0.9) * 5 + Math.sin(idx * 1.7) * 3;
                          const envelope = 10 + progress * 20 + wave;
                          h = Math.max(5, Math.min(36, envelope));
                          isStatic = false;
                        }
                        const delay = (i * 41) % 600;
                        return (
                          <div
                            key={i}
                            className="rounded-full"
                            style={{
                              backgroundColor: '#616161',
                              width: '2px',
                              animation: isStatic ? 'none' : `waveBar 0.6s ease-in-out ${delay}ms infinite alternate`,
                              height: `${h}px`,
                              opacity: isStatic ? 0.35 : 1,
                            }}
                          />
                        );
                      })}
                      <style>{`
                        @keyframes waveBar {
                          0% { transform: scaleY(0.4); opacity: 0.5; }
                          100% { transform: scaleY(1); opacity: 1; }
                        }
                      `}</style>
                    </div>
                  </div>

                  {/* Cancel (X) button */}
                  <button
                    onClick={cancelRecording}
                    disabled={isTranscribing}
                    className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center hover:bg-[#E5E7EB] transition-colors flex-shrink-0 disabled:opacity-50"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>

                  {/* Confirm (✓) button */}
                  <button
                    onClick={handleMicClick}
                    disabled={isTranscribing}
                    className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center hover:bg-[#E5E7EB] transition-colors flex-shrink-0 ml-1.5 disabled:opacity-50"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </button>
                </div>
              ) : (
                /* All elements inside one rounded container — `rounded-3xl`
                    keeps a fixed corner radius so the box stays a rounded
                    rectangle when the textarea grows past one line (was
                    rounded-full, which scaled the radius with height and
                    turned the multi-line box into a circle/oval). Staged
                    image thumbnails (if any) stack in their own row ABOVE
                    the input row, both inside this same bordered capsule —
                    same layout ChatGPT's own attach-image preview uses,
                    rather than floating outside/above the input box. */
                <div
                  className="relative w-full rounded-3xl border border-gray-200 bg-white"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {/* "/" file picker — a single floating card (search bar +
                      list) above the input capsule, same anchor point
                      ChatGPT/Notion-style slash menus use. Its own search box
                      drives everything below: empty shows folder navigation
                      (SharePoint Knowledge Area > Site groups need a click to
                      drill in, Standard/local files select on one click —
                      same split Sidebar.jsx renders), any typed text switches
                      to a global filename search across every site/folder.
                      Stays open across picks (see selectRestrictFile) so
                      checkmarks accumulate instead of the card closing after
                      each one. */}
                  {isFilePickerOpen && (
                    <div
                      ref={filePickerContainerRef}
                      className="absolute bottom-full left-0 mb-2 w-full rounded-2xl border border-gray-200 bg-white shadow-lg z-20 overflow-hidden"
                    >
                      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-gray-400">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                          ref={filePickerSearchRef}
                          type="text"
                          value={filePickerSearchText}
                          onChange={(e) => setFilePickerSearchText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              closeFilePicker();
                              return;
                            }
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (isPickerSearching) {
                                if (filePickerSearchResults[0]) {
                                  selectRestrictFile(filePickerSearchResults[0].file, filePickerSearchResults[0].areaLabel);
                                }
                              } else if (filePickerActiveFolder) {
                                if (filePickerSubfolders[0]) {
                                  enterFilePickerSubfolder(filePickerSubfolders[0].name);
                                } else if (filePickerFilesInFolder[0]) {
                                  selectRestrictFile(filePickerFilesInFolder[0], activeFolderGroup?.label);
                                }
                              } else if (filePickerTopEntries[0]) {
                                const firstEntry = filePickerTopEntries[0];
                                if (firstEntry.type === 'folder') enterFilePickerFolder(firstEntry.key);
                                else selectRestrictFile(firstEntry.file);
                              }
                            }
                          }}
                          placeholder={t('chatActions.filePickerSearchPlaceholder')}
                          className="flex-1 min-w-0 text-[14px] border-none focus:outline-none focus:ring-0 bg-transparent placeholder-gray-400"
                        />
                        <span className="flex-shrink-0 text-[11px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">
                          Esc
                        </span>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {isPickerSearching ? (
                          filePickerSearchResults.map((result) => (
                            renderPickerFileRow(result.file, result.areaLabel, result.folderPath)
                          ))
                        ) : filePickerActiveFolder ? (
                          <>
                            <button
                              type="button"
                              onClick={backToFilePickerFolders}
                              className="w-full flex items-center gap-1.5 px-4 py-2 text-left text-[13px] text-gray-500 hover:bg-gray-50 transition-colors border-b border-gray-100"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12"/>
                                <polyline points="12 19 5 12 12 5"/>
                              </svg>
                              <span className="truncate">
                                {[activeFolderGroup?.label, ...filePickerSubfolderPath].filter(Boolean).join(' / ')}
                              </span>
                            </button>
                            {filePickerSubfolders.map((folder) => (
                              <button
                                key={folder.name}
                                type="button"
                                onClick={() => enterFilePickerSubfolder(folder.name)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-gray-400">
                                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>
                                <span className="text-[14px] text-gray-800 truncate flex-1">{folder.name}</span>
                              </button>
                            ))}
                            {filePickerFilesInFolder.map((file) => (
                              renderPickerFileRow(file, activeFolderGroup?.label)
                            ))}
                          </>
                        ) : (
                          filePickerTopEntries.map((entry) => (
                            entry.type === 'folder' ? (
                              <button
                                key={entry.key}
                                type="button"
                                onClick={() => enterFilePickerFolder(entry.key)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-gray-400">
                                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>
                                <span className="text-[14px] text-gray-800 truncate flex-1">{entry.label}</span>
                                <span className="text-[12px] text-gray-400 flex-shrink-0">{entry.count}</span>
                              </button>
                            ) : (
                              renderPickerFileRow(entry.file, null)
                            )
                          ))
                        )}
                      </div>
                    </div>
                  )}
                  {selectedRestrictFiles.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3">
                      {selectedRestrictFiles.map((entry, index) => (
                        <span
                          key={entry.file.sharepoint_item_id || entry.file.id || entry.file.filename}
                          // Full path (Knowledge Area + Site + nested SharePoint
                          // folders + filename) can get long — without a real
                          // cap here the chip just grew past the input box's
                          // edge with no ellipsis instead of truncating.
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-gray-100 text-[13px] text-gray-700 max-w-full sm:max-w-[280px]"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          {/* Filename only (path was truncating first, hiding it) — full path in the tooltip. */}
                          <span className="truncate min-w-0 flex-1" title={getSelectedFileDisplayPath(entry.file, entry.areaLabel)}>{entry.file.filename}</span>
                          <button
                            type="button"
                            onClick={() => removeRestrictFile(index)}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-0.5"
                            aria-label={t('chatActions.filePickerClear')}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {pendingImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 px-4 pt-3">
                      {pendingImages.map((pending, index) => (
                        <div key={index} className="relative w-14 h-14 flex-shrink-0 group">
                          <img
                            src={pending.previewUrl}
                            alt={pending.file.name}
                            className="w-14 h-14 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePendingImage(index)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-700 text-white flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                            aria-label={t('chatActions.removeFile')}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {analysisModeOn && codeInterpreterEnabled && !isChatRoute && (
                    <div className="flex items-center justify-between gap-3 px-4 pt-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[12px] font-semibold flex-shrink-0"
                        style={{ backgroundColor: ANALYSIS_BLUE_SOFT, color: ANALYSIS_BLUE }}
                      >
                        <SquareTerminal size={12} />
                        {t('dataAnalysis.modeName')}
                      </span>
                      <span className="text-[11px] text-gray-400 truncate">{t('dataAnalysis.composerMeta')}</span>
                    </div>
                  )}
                  {wordModeOn && !isChatRoute && (
                    <div className="flex items-center justify-between gap-3 px-4 pt-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[12px] font-semibold flex-shrink-0"
                        style={{ backgroundColor: `${primaryColor}14`, color: primaryColor }}
                      >
                        <WordIcon size={12} />
                        {t('chatActions.wordModeLabel')}
                      </span>
                      <span className="text-[11px] text-gray-400 truncate">{t('chatActions.wordComposerMeta')}</span>
                    </div>
                  )}
                  <div className="flex items-center w-full pl-4 pr-2 py-2">
                  {/* Plus icon on left - triggers file upload — hidden when local uploads are disabled */}
                  {allowLocalFiles && (
                    <button
                      type="button"
                      onClick={handleFileClick}
                      disabled={isProcessing}
                      className={`text-gray-400 hover:text-gray-600 flex-shrink-0 ${isProcessing ? 'cursor-wait' : ''}`}
                      title={t('chatActions.uploadFile')}
                    >
                      {isProcessing ? (
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                      )}
                    </button>
                  )}

                  {/* Input field — textarea so Shift+Enter inserts a newline
                      while plain Enter still sends the message. */}
                  <textarea
                    ref={textareaRef}
                    name="prompt"
                    id="prompt"
                    rows={1}
                    value={chatInput}
                    className={`flex-1 mx-3 text-[14px] border-none focus:outline-none focus:ring-0 bg-transparent placeholder-gray-400 resize-none leading-5 max-h-32 overflow-y-auto ${isChatRoute ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={isChatRoute ? t('chatActions.chatDisabled') : t('chatActions.askPlaceholder')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Leading "/" opens the picker instead of being typed —
                      // it never sits in this textarea; the picker's own
                      // search box (below) takes over from here.
                      if (!isFilePickerOpen && !isPureWebOnly && value.startsWith('/')) {
                        setIsFilePickerOpen(true);
                        setFilePickerSearchText(value.slice(1));
                        setChatInput('');
                        e.target.style.height = 'auto';
                        return;
                      }
                      setChatInput(value);
                      // Auto-grow with content, capped via max-h-32 above.
                      e.target.style.height = 'auto';
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        // Typing itself is never blocked — handleSendMessage
                        // itself is the single place that holds off the
                        // actual submit until the previous response settles.
                        handleSendMessage(chatInput, null);
                      }
                    }}
                    onPaste={handlePaste}
                    disabled={isChatRoute}
                  />

                  {/* Visible "/" picker trigger — construction/quality users
                      won't discover a bare "/" convention on their own. Toggle
                      button: click opens/closes the picker, and looks
                      "pressed" (org's own dynamic color, same one the
                      dropdown's checkmarks use) the whole time it's open —
                      clicking outside or Escape closes it and this reverts
                      right along with isFilePickerOpen, no separate state
                      needed. Deliberately a BARE icon with no sizing/centering
                      classes of its own — same recipe as the mic button right
                      after it (flex-shrink-0 mr-2 + a plain same-size SVG), so
                      the two sit on the exact same baseline instead of a
                      fixed-size box subtly shifting this one. The active-state
                      "pill" is a decorative, absolutely-positioned circle
                      behind the icon — rendered first so DOM order alone puts
                      it behind (a negative z-index instead sank it behind the
                      whole white input capsule's own background, since
                      `relative` alone doesn't give the button its own
                      stacking context — invisible, not just faint). Never
                      touches layout/alignment either way. An SVG diagonal
                      line instead of a literal "/" character — font glyphs
                      render visually off-center for a slash, a hand-drawn
                      line centers exactly. */}
                  {/* Hidden for pure Web-only agents — no document knowledge base to restrict to. */}
                  {!isPureWebOnly && (
                  <button
                    ref={filePickerTriggerRef}
                    type="button"
                    onClick={() => (isFilePickerOpen ? closeFilePicker() : setIsFilePickerOpen(true))}
                    disabled={isChatRoute}
                    className={`relative flex-shrink-0 mr-2 w-5 h-5 flex items-center justify-center leading-none ${
                      isChatRoute
                        ? 'opacity-70 cursor-not-allowed text-gray-300'
                        : isFilePickerOpen
                          ? ''
                          : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={isFilePickerOpen && !isChatRoute ? { color: primaryColor } : undefined}
                    title={t('chatActions.filePickerTrigger')}
                  >
                    {isFilePickerOpen && !isChatRoute && (
                      <span
                        className="absolute -inset-1.5 rounded-full"
                        style={{ backgroundColor: `${primaryColor}33` }}
                      />
                    )}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round">
                      <line x1="17" y1="5" x2="7" y2="19" />
                    </svg>
                  </button>
                  )}

                  {/* Word toggle — only shown when this agent has Word
                      generation enabled (Tools tab). Per-message, resets
                      after sending (see wordModeOn). Same "/" button recipe:
                      a bare icon plus a decorative, absolutely-positioned
                      pill in the app's own dynamic colour behind it when
                      active (rendered first so DOM order alone puts it
                      behind) — not a ring/border, which visually bled
                      outward into the neighbouring icons' space. mr-3 (not
                      the -2 the other icons use) gives this one a touch more
                      breathing room on both sides since WordIcon is a
                      filled colour glyph, not a thin monochrome line. */}
                  {codeInterpreterEnabled && (
                  <button
                      type="button"
                      role="switch"
                      aria-checked={analysisModeOn}
                      aria-label={t('dataAnalysis.modeName')}
                      onClick={() => {
                        const next = !analysisModeOn;
                        setAnalysisMode(next);
                        if (next) setWordModeOn(false);
                      }}
                      disabled={isChatRoute}
                      title={t('dataAnalysis.tooltip')}
                      className={`flex-shrink-0 mr-2 inline-flex items-center justify-center w-7 h-7 rounded-lg border leading-none transition-colors ${
                        analysisModeOn && !isChatRoute
                          ? ''
                          : 'border-transparent text-gray-400 hover:text-gray-600'
                      } ${isChatRoute ? 'opacity-40 cursor-not-allowed' : ''}`}
                      style={analysisModeOn && !isChatRoute ? { backgroundColor: ANALYSIS_BLUE_SOFT, borderColor: ANALYSIS_BLUE_BORDER, color: ANALYSIS_BLUE } : undefined}
                    >
                      <SquareTerminal size={18} />
                    </button>
                  )}

                  {wordEnabled && (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={wordModeOn}
                      onClick={() => {
                        const next = !wordModeOn;
                        setWordModeOn(next);
                        if (next) setAnalysisMode(false);
                      }}
                      disabled={isChatRoute}
                      className={`relative flex-shrink-0 ml-1 mr-3 w-5 h-5 flex items-center justify-center leading-none ${
                        isChatRoute ? 'opacity-40 cursor-not-allowed' : ''
                      }`}
                      title={t('chatActions.wordToggle')}
                    >
                      {wordModeOn && !isChatRoute && (
                        <span
                          className="absolute -inset-1.5 rounded-full"
                          style={{ backgroundColor: `${primaryColor}66` }}
                        />
                      )}
                      <WordIcon size={20} className="relative" />
                    </button>
                  )}

                  {/* Microphone icon / Transcribing spinner */}
                  <button
                    onClick={handleMicClick}
                    type="button"
                    disabled={isChatRoute || isTranscribing}
                    className={`flex-shrink-0 mr-2 ${isChatRoute || isTranscribing ? 'opacity-70 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                    title={t('chatActions.voiceInput')}
                  >
                    {isTranscribing ? (
                      <svg className="animate-spin h-5 w-5" style={{ color: primaryColor }} viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                    )}
                  </button>

                  {/* Send button - inside the container */}
                  <button
                    onClick={() => handleSendMessage(chatInput, null)}
                    type="button"
                    title={hasPendingFiles ? t('chatActions.filesStillProcessing') : undefined}
                    disabled={isChatRoute || isRecording || isTranscribing || isResponsePending || hasPendingFiles || hasNothingToSend || isImageAnalyzing}
                    className={`flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full text-white transition-colors ${
                      (isChatRoute || isRecording || isTranscribing || isResponsePending || hasPendingFiles || hasNothingToSend || isImageAnalyzing)
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'hover:opacity-90'
                    }`}
                    style={{ backgroundColor: (isChatRoute || isRecording || isTranscribing || isResponsePending || hasPendingFiles || hasNothingToSend || isImageAnalyzing) ? undefined : primaryColor }}
                  >
                    {isImageAnalyzing ? (
                      // Spins only while the just-attached image is still
                      // uploading/being analyzed — see isImageAnalyzing above.
                      // Turns back into the plain send arrow (still disabled,
                      // via isResponsePending) the moment the question is
                      // actually sent, since the answer-generation progress
                      // shown elsewhere in the chat takes over from there.
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 10l7-7m0 0l7 7m-7-7v18"
                        />
                      </svg>
                    )}
                  </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <Modal
            isOpen={isModalOpen}
            onClose={closeModal}
            onYes={handleYes}
            onNo={handleNo}
            title={t('chatActions.newAnalysisTitle')}
          >
            <ToastContainer/>
            <p>
              {t('chatActions.newAnalysisConfirm')}
            </p>
          </Modal>
        </div>
      </div>
    </footer>
  );
};

export default ChatInput;